import { Link, useNavigate } from 'react-router-dom';
import type { AuditRecruiterResult, AuditResultsData, Envelope } from '../../api/types.js';
import { auditProvenance } from '../Breakdown/provenance.js';
import { ProvenanceFootnote } from '../Breakdown/Breakdown.js';
import { DataTable, type Column } from '../DataTable/DataTable.js';
import { Disclosure } from '../Disclosure/Disclosure.js';
import { EmptyState } from '../States/States.js';
import { color, font, fontSize, radius } from '../../theme/tokens.js';
import { BackLink } from './AuditRuns.js';
import {
  RegionPill,
  StatusPill,
  Cell,
  TrendGlyph,
  cellTrend,
  excludedReasonText,
  figure,
  figureExact,
  formatRunDate,
  share,
  type Trend,
} from './shared.js';

/**
 * State 3 — one run's results (blueprint lines 1047–1101, PRD-Audit §6.5).
 *
 * Every row is an in-scope recruiter the audit CHECKED (the server has already dropped everyone
 * else, and counts — never lists — those it had nothing to check, AU-16). The Focus column shows
 * the figure the rule reads: a share for Target/Regional Subs, a count elsewhere.
 *
 * No AI Analysis button: one analyst, not page-scoped (owner direction 2026-09-27, AU-08).
 */

function RunNav({ data }: { data: AuditResultsData }) {
  const navigate = useNavigate();
  const go = (date: string | null) => date && navigate(`/audit/${encodeURIComponent(data.audit.id)}/${date}`);
  const button = (date: string | null, label: string, path: string) => (
    <button
      type="button"
      onClick={() => go(date)}
      disabled={!date}
      title={`${label}: ${date ? formatRunDate(date) : 'none'}`}
      aria-label={`${label}: ${date ? formatRunDate(date) : 'none'}`}
      style={{
        padding: '11px 14px',
        background: 'transparent',
        border: 'none',
        cursor: date ? 'pointer' : 'default',
        opacity: date ? 1 : 0.35,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={color.inkBody} strokeWidth="2.2" aria-hidden="true">
        <path d={path} />
      </svg>
    </button>
  );
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        background: color.surface,
        border: `1px solid ${color.border}`,
        borderRadius: radius['2xl'],
        overflow: 'hidden',
        flex: 'none',
      }}
    >
      {button(data.previousRunDate, 'Previous run', 'm15 18-6-6 6-6')}
      <div style={{ width: 1, alignSelf: 'stretch', background: color.border }} />
      {button(data.nextRunDate, 'Next run', 'm9 18 6-6-6-6')}
    </div>
  );
}

/** One figure cell: face, arrow, and a panel with the exact figures and the provenance footer. */
function FigureCell({
  label,
  face,
  exact,
  trend,
  ink,
  rule,
}: {
  label: string;
  face: string;
  exact: string;
  trend: Trend;
  ink: string;
  rule: string;
}) {
  return (
    <Disclosure
      accessibleName={`${label}: ${exact}. ${trend.tooltip}`}
      panel={
        <span>
          <strong>{label}</strong>
          <span style={{ display: 'block' }}>{exact}</span>
          <span style={{ display: 'block', marginTop: 3 }}>{trend.tooltip}</span>
          <ProvenanceFootnote provenance={auditProvenance(rule)} />
        </span>
      }
      style={{ display: 'inline-flex', gap: 5, alignItems: 'center', justifyContent: 'flex-end' }}
    >
      <span className="mono" style={{ fontFamily: font.mono, fontSize: fontSize.bodyStrong, fontWeight: 600, color: ink }}>
        {face}
      </span>
      <TrendGlyph trend={trend} />
    </Disclosure>
  );
}

/** The roster line under the run card — and a warning when the roster itself is stale. */
export function rosterNote(envelope: Pick<Envelope<unknown>, 'asOf' | 'syncStatus' | 'staleness'>): string {
  const date = envelope.asOf ? formatRunDate(envelope.asOf) : 'an unknown date';
  const stale = envelope.syncStatus === 'degraded' || (envelope.staleness && envelope.staleness.level !== 'fresh');
  return stale
    ? `Recruiters in scope: the alignment roster as of ${date} — the last daily refresh failed, so this roster may be out of date.`
    : `Recruiters in scope: the alignment roster as of ${date}.`;
}

export function AuditResults({
  data,
  envelope,
}: {
  data: AuditResultsData;
  envelope: Pick<Envelope<unknown>, 'asOf' | 'syncStatus' | 'staleness'>;
}) {
  const { audit, labels, run } = data;
  const reporting = audit.kind === 'reporting';
  const uncatalogued = audit.kind === 'uncatalogued';
  const shareFocus = labels.focus.reads === 'share';
  const rule = audit.description ?? 'An audit this page does not describe yet; figures use the service’s labels.';

  const baseOf = (r: AuditRecruiterResult) => r.base;
  const focusOf = (r: AuditRecruiterResult) => (shareFocus ? r.focusShare : r.focusCount);
  const prevFocus = (r: AuditRecruiterResult) =>
    r.previous ? (shareFocus ? r.previous.focusShare : r.previous.focusCount) : undefined;

  const trendFor = (r: AuditRecruiterResult, which: 'base' | 'focus') =>
    cellTrend({
      current: which === 'base' ? baseOf(r) : focusOf(r),
      previous: which === 'base' ? r.previous?.base : prevFocus(r),
      direction: which === 'base' ? audit.direction.base : audit.direction.focus,
      previousRunDate: data.previousRunDate,
      hadPreviousRow: r.previous !== null,
      excludedNowOrThen: r.status === 'excluded' || (r.previous?.excluded ?? false),
      render: which === 'focus' && shareFocus ? (v) => share(v) : (v) => figureExact(v),
    });

  const columns: Array<Column<AuditRecruiterResult>> = [
    {
      key: 'name',
      label: 'Recruiter',
      fullLabel: 'Recruiter',
      width: '1.6fr',
      align: 'left',
      sortable: true,
      sortValue: (r) => r.name ?? '',
      render: (r) => (
        <Cell>
          <span style={{ fontWeight: 600, color: color.ink }}>{r.name ?? `Recruiter ${r.recruiterId}`}</span>
        </Cell>
      ),
    },
    {
      key: 'region',
      label: 'Region',
      fullLabel: 'Recruiter’s assigned region',
      width: '.6fr',
      align: 'left',
      sortable: true,
      sortValue: (r) => r.region,
      render: (r) => (
        <Cell>
          <RegionPill region={r.region} serviceRegion={r.serviceRegion} />
        </Cell>
      ),
    },
    {
      key: 'base',
      label: labels.base.short,
      fullLabel: labels.base.full,
      width: '1.1fr',
      align: 'right',
      sortable: true,
      sortValue: (r) => r.base,
      render: (r) => (
        <Cell align="right"><FigureCell
          label={labels.base.full}
          face={figure(r.base)}
          exact={r.status === 'excluded' ? excludedReasonText(r.excludedReason) : figureExact(r.base)}
          trend={trendFor(r, 'base')}
          ink={color.inkNumeric}
          rule={rule}
        /></Cell>
      ),
    },
    {
      key: 'focus',
      label: labels.focus.short,
      fullLabel: labels.focus.full,
      width: '1.1fr',
      align: 'right',
      sortable: true,
      sortValue: (r) => focusOf(r),
      render: (r) => {
        const exact =
          r.status === 'excluded'
            ? excludedReasonText(r.excludedReason)
            : shareFocus
              ? `${figureExact(r.focusCount)} of ${figureExact(r.base)} · ${share(r.focusShare)}`
              : r.focusShare !== null
                ? `${figureExact(r.focusCount)} · ${share(r.focusShare)} of ${figureExact(r.base)}`
                : figureExact(r.focusCount);
        return (
          <Cell align="right"><FigureCell
            label={labels.focus.full}
            face={shareFocus ? share(r.focusShare) : figure(r.focusCount)}
            exact={exact}
            trend={trendFor(r, 'focus')}
            ink={r.status === 'flagged' ? color.negative : color.inkNumeric}
            rule={rule}
          /></Cell>
        );
      },
    },
  ];

  if (!reporting) {
    columns.push({
      key: 'status',
      label: 'Status',
      fullLabel: 'Whether this recruiter passed or was flagged for this audit',
      width: '.8fr',
      align: 'right',
      sortable: true,
      sortValue: (r) => (r.status === 'flagged' ? 0 : r.status === 'pass' ? 1 : r.status === null ? 1 : 2),
      // Uncatalogued audits never read Pass — the server sends `null` for their unflagged rows.
      render: (r) => (
        <Cell align="right">
          <StatusPill status={uncatalogued && r.status === 'pass' ? null : r.status} reason={r.excludedReason} />
        </Cell>
      ),
    });
  }

  return (
    <div>
      <BackLink to={`/audit/${encodeURIComponent(audit.id)}`} label={`${audit.name} · Runs`} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
        <div
          style={{
            flex: 1,
            background: color.surface,
            border: `1px solid ${color.border}`,
            borderRadius: radius['6xl'],
            padding: '16px 20px',
          }}
        >
          <div style={{ fontSize: fontSize.bodyStrong, fontWeight: 700, color: color.ink }}>
            Run — {formatRunDate(data.runDate)}
          </div>
          <Disclosure
            accessibleName={`${run?.summary ?? 'No run on this date.'} ${rosterNote(envelope)}`}
            panel={
              <span>
                <span style={{ display: 'block' }}>{rosterNote(envelope)}</span>
                {run && run.outOfScope > 0 ? (
                  <span style={{ display: 'block', marginTop: 4 }}>
                    {run.outOfScope} audited recruiter{run.outOfScope === 1 ? '' : 's'} outside the seven
                    regions {run.outOfScope === 1 ? 'is' : 'are'} not shown.
                  </span>
                ) : null}
                <ProvenanceFootnote provenance={auditProvenance(rule)} />
              </span>
            }
          >
            <span style={{ fontSize: fontSize.control, color: color.inkFaint, marginTop: 3, display: 'block' }}>
              {run?.summary ?? 'No run on this date.'}
            </span>
          </Disclosure>
        </div>
        <RunNav data={data} />
      </div>

      {run === null ? (
        <EmptyState
          title={`No run on ${formatRunDate(data.runDate)}.`}
          detail="The audit service holds no results for this audit on this date."
          action={
            <span style={{ display: 'flex', gap: 14, justifyContent: 'center' }}>
              {data.nearest.older ? (
                <Link to={`/audit/${encodeURIComponent(audit.id)}/${data.nearest.older}`}>
                  Earlier run: {formatRunDate(data.nearest.older)}
                </Link>
              ) : null}
              {data.nearest.newer ? (
                <Link to={`/audit/${encodeURIComponent(audit.id)}/${data.nearest.newer}`}>
                  Later run: {formatRunDate(data.nearest.newer)}
                </Link>
              ) : null}
            </span>
          }
        />
      ) : data.rows.length === 0 ? (
        <EmptyState title="No in-scope recruiter was checked on this run." detail={rosterNote(envelope)} />
      ) : (
        <DataTable
          columns={columns}
          rows={data.rows}
          rowKey={(r) => String(r.recruiterId)}
          caption={`${audit.name} — run of ${formatRunDate(data.runDate)}`}
          rowTint={(r) => (r.status === 'flagged' && !reporting ? color.flaggedRow : undefined)}
        />
      )}
    </div>
  );
}
